import {
    ConsentLevel,
    useConsentGiven,
    useScriptWithConsent,
} from '@news-mono/web-common'
import { IFrameObject, iframeResizer } from 'iframe-resizer'
import React from 'react'
import { Iframe } from '../../../content/Iframe/Iframe'
import { ConsentFallback } from '../../../tracking/ConsentFallback/ConsentFallback'

export type ShortStackEventName =
    | 'changed.content'
    | 'error.form'
    | 'exception.form'
    | 'sending.form'
    | 'success.form'
    | 'success.invite'
    | 'liked'
    | 'success.poll'
    | 'error.poll'
    | 'cancel.share'
    | 'changed.video'
    | 'play.video'
    | 'success.vote'

export interface ShortStackMessageEvent extends MessageEvent {
    data: {
        namespace: string
        originator: string
        timestamp: number
        type: string
        name: ShortStackEventName
    }
}

export interface ShortstackProps {
    link: string
}

const embedLink = '/embed'
function processEmbedLink(link: string) {
    if (link.indexOf(embedLink) === -1) {
        return `${link}`
    }

    return link
}

interface EmbedParams {
    scriptUrl: string
    embedId: string
}

export function processEmbedParams(link: string): EmbedParams | undefined {
    const decodedUri = new URL(link)
    const embedScriptUrl = decodedUri.searchParams.get('embedScriptUrl')
    const embedId = decodedUri.searchParams.get('embedId')

    if (embedScriptUrl && embedId) {
        return {
            embedId,
            scriptUrl: embedScriptUrl,
        }
    }
}

function isShortStackMessage(
    event: ShortStackMessageEvent | MessageEvent,
): event is ShortStackMessageEvent {
    return (
        event.origin.indexOf('shortstack') >= 0 &&
        event.data &&
        event.data.originator === 'shortstack'
    )
}

export const Shortstack: React.FC<ShortstackProps> = ({ link }) => {
    const customEmbedParams = processEmbedParams(link)
    const embedScriptUrl = customEmbedParams?.scriptUrl
    const embedId = customEmbedParams?.embedId

    const iframeEmbedSrc = customEmbedParams
        ? `${processEmbedLink(link)}&`
        : `${processEmbedLink(link)}?`

    // When the scriptUrl and embedId are provided, we want to let the javascript render the iframe, we just give it an empty div to populate
    const isEnhancedEmbed = customEmbedParams !== undefined

    const [result] = useScriptWithConsent({
        consentRequiredLevel: ConsentLevel.Essential | ConsentLevel.Analytics,
        consentMet: {
            src: embedScriptUrl ? embedScriptUrl : '',
        },
    })

    const iframeElement = React.useRef<HTMLIFrameElement>(null)
    const shortStackEventList = React.useMemo<ShortStackMessageEvent[]>(
        () => [],
        [],
    )
    const iframeResizerComponents = React.useMemo<IFrameObject[]>(() => [], [])

    const handleIframeMessage = React.useCallback(
        (event: ShortStackMessageEvent | MessageEvent) => {
            function fixPageScrollOffset() {
                setTimeout(() => {
                    if (iframeElement.current) {
                        window.scrollTo({
                            top:
                                iframeElement.current.getBoundingClientRect()
                                    .top + window.pageYOffset,
                            behavior: 'smooth',
                        })
                    }
                }, 0)
            }

            if (!isShortStackMessage(event) || !iframeElement.current) {
                return
            }

            // Sometimes on changing content, the iframe will change height and leave the viewer at a bad scroll
            // position. If this is the case, we fix it up. This is done by looking for content changes after submissions
            const prevEvent =
                shortStackEventList[shortStackEventList.length - 1]
            const contentIsChanging = [
                'success.form',
                'success.invite',
                'success.vote',
            ]
            const shouldFixScrollOffset =
                prevEvent &&
                contentIsChanging.indexOf(prevEvent.data.name) >= 0 &&
                event.data.name === 'changed.content'

            if (shouldFixScrollOffset) {
                fixPageScrollOffset()
            }

            shortStackEventList.push(event)
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [iframeElement, shortStackEventList],
    )

    React.useEffect(() => {
        if (iframeElement.current) {
            // this will attach an instance of an iframeResizer class to the iframe element
            const iframeComponents = iframeResizer({}, iframeElement.current)
            iframeResizerComponents.push(
                ...iframeComponents.map((component) => component.iFrameResizer),
            )

            addEventListener('message', handleIframeMessage)
        }

        return () => {
            removeEventListener('message', handleIframeMessage)
            if (iframeResizerComponents) {
                iframeResizerComponents.forEach((iframeResizerInstance) =>
                    iframeResizerInstance.close(),
                )
            }
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [iframeElement, shortStackEventList])

    const consentGiven = useConsentGiven({
        consentRequiredLevel: ConsentLevel.Analytics | ConsentLevel.Advertising,
    })

    if (!consentGiven) {
        return <ConsentFallback />
    }

    return isEnhancedEmbed ? (
        <div
            data-stck-id={embedId}
            data-stck-src={`${iframeEmbedSrc}embed=4&script=1`}
            data-stck-style="border:none;width:100%;height:100%;"
        />
    ) : (
        <Iframe
            link={`${iframeEmbedSrc}embed=1&v_offset=0&autoscroll_p=1`}
            innerRef={iframeElement}
            height="auto"
            marginBottom="md"
            allow="autoplay"
            sandbox="allow-modals allow-forms allow-scripts allow-same-origin allow-popups allow-top-navigation allow-top-navigation-by-user-activation allow-downloads allow-popups-to-escape-sandbox"
            allowFullScreen
            data-hj-allow-iframe
            id={embedId ? embedId : undefined}
            className="embed_frame"
        />
    )
}

export default Shortstack
